<template>
  <div
    ref='filterOuterRef'
    :class='{
      "dropdown-filter": !isMobile || !isActive,
      "mobile-dropdown-filter": isMobile && isActive && !isForceDesktopStyle,
      "is-force-desktop": isForceDesktopStyle,
      [name]: true
    }'
  >
    <LabelButton
      :is-active='hasValue'
      :is-disabled='isDisabled'
      :is-chevron='!hasValue'
      :is-chevron-active='isActive'
      :is-reset='hasValue'
      @click='toggleIsActive'
      @reset='emit("filter:reset")'
    >
      {{ label }}
      <template v-if='hasValue'>
        : {{ checkedOptionsCount }}
      </template>
    </LabelButton>
    <TransitionHeight :duration='transitionHeightDuration'>
      <div
        v-if='isActive'
        class='filter-options'
      >
        <div class='inner-padding'>
          <div
            class='inner-background'
            :class="[{ 'is-search': isSearch }]"
          >
            <div v-if='isSearch' class='filter-search'>
              <SearchIcon class='search-icon' />
              <input
                ref='searchInput'
                v-model='searchValue'
                :placeholder="searchI18nT(`index.label.${name}`)"
              >
            </div>
            <div
              v-if='searchValue && !searchedOptions.length'
              class='filters-group nothing-found'
            >
              <div class='title'>{{ searchI18nT(`index.nothing_found.title.${name}`) }}</div>
              <div class='note'>{{ searchI18nT(`index.nothing_found.note`) }}</div>
            </div>
            <template v-else>
              <div
                v-if='isSearch && searchedOptionsChecked.length'
                class='filters-group'
              >
                <Option
                  v-for='option in searchedOptionsChecked'
                  v-bind='option'
                  :key='option.label'
                  :option='option'
                  :i18n-t='searchI18nT'
                  @update:is-checked='syncOption(option, $event)'
                />
              </div>
              <div class='filters-group'>
                <Option
                  v-for='option in searchedOptions'
                  v-bind='option'
                  :key='option.label'
                  :option='option'
                  :i18n-t='searchI18nT'
                  @update:is-checked='syncOption(option, $event)'
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </TransitionHeight>
  </div>
</template>

<script>
export default {
  name: 'DropdownFilter',
  inheritAttrs: false
};
</script>

<script setup>
import delay from 'delay';
import { ref, computed, watchEffect } from 'vue';

import Option from './option';
import LabelButton from '../label_button';
import TransitionHeight from '../transition_height';
import { useMobileDetect } from '../../composables/mobile_detect';

import SearchIcon from '@/../images/vue/storages_map-v4/dropdown_filter/search.svg?inline';
import UncheckIcon from '@/../images/vue/storages_map-v4/dropdown_filter/uncheck.svg?inline';

import isDescandant from '@/helpers/is_descendant';

const props = defineProps({
  name: { type: String, required: true },
  isRadio: { type: Boolean, required: true },
  isActive: { type: Boolean, required: true },
  label: { type: String, required: true },
  options: { type: Array, required: true },
  isCheckedByDefault: { type: Boolean, required: false, default: false },
  isSingleLabel: { type: Boolean, required: false, default: false },
  isSearch: { type: Boolean, required: false, default: false },
  searchI18nT: { type: Function, required: false, default: undefined },
  isForceDesktopStyle: { type: Boolean, required: false, default: false }
});
const emit = defineEmits(['filter:update', 'filter:reset']);

const searchInput = ref(null);
const filterOuterRef = ref(null);
const searchValue = ref('');

const { isMobile } = useMobileDetect();
const transitionHeightDuration = computed(() => (
  isMobile.value ? 1 : 250
));

const searchedOptions = computed(() => (
  searchValue.value ?
    props.options.filter(option => (
      option.label.toLowerCase().includes(searchValue.value.toLowerCase())
    )) :
    props.options
));
const searchedOptionsChecked = computed(() => (
  searchedOptions.value.filter(v => v.isChecked)
));

const checkedOptionsCount = computed(() => (
  props.options.filter(v => v.isChecked).length
));
const isDisabled = computed(() => props.options.length < 2);
const label = computed(() => {
  if (checkedOptionsCount.value === 1 && props.isSingleLabel) {
    return props.options.find(v => v.isChecked).label;
  }

  return isDisabled.value && props.options.length === 1 ?
    props.options[0].label :
    props.label;
});
const hasValue = computed(() => (
  checkedOptionsCount.value > 0 && (
    !props.isCheckedByDefault ||
      checkedOptionsCount.value !== props.options.length
  ) &&
    !(checkedOptionsCount.value === 1 && props.isSingleLabel)
));

if (props.isSearch) {
  watchEffect(async () => {
    if (props.isActive && searchInput.value) {
      await delay(250);

      if (props.isActive) {
        searchInput.value.focus();
      }
    }
  });
}

function toggleIsActive() {
  if (isDisabled.value) { return; }

  if (props.isActive) {
    collapseFilter();
  } else {
    emit('filter:update', { isActive: true });
    bindCollapseFilter();
  }
}

function syncOption(option, isChecked) {
  emit('filter:update', {
    options: props.options.map(propOption => (
      {
        ...propOption,
        isChecked: option === propOption ?
          isChecked :
          (isChecked && props.isRadio ? false : propOption.isChecked)
      }
    ))
  });
}

function collapseFilter(event) {
  if (!props.isActive) { return; }

  if (event && isDescandant(event.target, filterOuterRef.value)) {
    bindCollapseFilter();
    return;
  }

  emit('filter:update', { isActive: false });
}

function bindCollapseFilter() {
  if (!isMobile.value || props.isForceDesktopStyle) {
    addEventListener('click', collapseFilter, { capture: true, once: true });
  }
}
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/ltr_rtl'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'

$filter-padding: rem(8px)

.dropdown-filter
  position: relative

  .filter-options
    box-sizing: content-box
    min-width: 100%
    transition: height var(--transition-duration)

    +lte_ipad
      .inner-padding
        padding-bottom: 0
        +padding-left_ltr(rem(24px))
        +padding-right_ltr(rem(24px))
        padding-top: rem(16px)

    +gte_laptop
      margin-bottom: 0
      +margin-left_ltr(-32px)
      +margin-right_ltr(0)
      margin-top: -12px + 8px
      position: absolute
      z-index: 3

      .inner-padding
        padding: rem(12px) rem(32px)

      .inner-background
        background: #ffffff
        border-radius: rem(16px)
        border: 1px solid #eaeaea
        display: flex
        flex-direction: column
        overflow-y: scroll

        &.is-search
          height: 480px

.is-force-desktop
  position: relative

  .filter-options
    +lte_ipad
      position: absolute !important
      margin-top: rem4(10px)
      z-index: 3

      .inner-background
        background: #ffffff
        border-radius: rem4(16px)
        border: 1px solid #eaeaea
        box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.1)
        display: flex
        flex-direction: column

.mobile-dropdown-filter,
.mobile-dropdown-filter .filter-options,
.mobile-dropdown-filter .inner-padding,
.mobile-dropdown-filter .inner-background
  display: flex
  flex-direction: column
  flex-grow: 1

.filter-search
  display: flex
  gap: rem(8px)

  +lte_ipad
    background: #f3f4f6
    border-radius: rem(45px)
    padding: rem(10px) rem(16px)
    margin: 0 var(--container-padding-x) rem(8px)

  +gte_laptop
    padding: rem(12px) rem(20px)

  .search-icon
    height: rem(21px)
    width: rem(20px)

  input
    border: none
    text-overflow: ellipsis
    width: 100%

    +lte_ipad
      background: #f3f4f6

    +gte_laptop
      background: #fff

svg.is-new
  height: rem(16px)
  width: rem(16px)

.nothing-found
  align-items: center
  display: flex
  flex-direction: column
  flex-grow: 1
  justify-content: center
  padding: rem(20px) var(--container-padding-x)

  .title
    color: var(--text-strong)
    font-size: rem(14px)
    font-weight: 700
    line-height: 1.3
    margin-bottom: rem(4px)
    text-align: center

  .note
    color: var(--text-secondary)
    font-size: rem(14px)
    line-height: 1.3
    text-align: center

.filters-group
  +lte_ipad
    &:not(:first-child):not(:last-child)
      border-bottom: 1px solid #eaeaea
      padding-bottom: rem(8px)
      margin-bottom: rem(8px)

  +gte_laptop
    padding: $filter-padding

    &:not(:first-child)
      border-top: 1px solid #eaeaea
</style>
